import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CAlert, CButton, CInputGroup, CSpinner } from '@coreui/react';
import CustomDatePicker from '../../../../../general/CustomDatePickerRange';
import dayjs from 'dayjs';
import CLabel from '../../../../../migration/CLabel';
import CFormGroup from '../../../../../migration/CFormGroup';
import ViewReviewAnalyse from '../Components/ViewReviewAnalyse';
import { callTokenApi } from '../../../../../../apiCaller';
import CPagination from '../../../../../migration/pagination/CPagination';
import { toast } from 'react-toastify';
import { HoverInfoIconTooltip } from '../../../../../general/custom-tooltips';
import { API_AI_INSIGHTS_JOB_RERUN } from '../../../../../../constants';
import { subscribe, unsubscribe } from '../../../../../../containers/subscriber/eventBus';

const ReviewAnalyse = ({attributes}) => {
   const { templateId, templateName, models } = attributes;
    const { timeZone, id: accountId } = useSelector((state) => state.subscriber.activeAccount);
    // Calculate initial dates only once
    const defaultEndDate = useMemo(() => dayjs().tz(timeZone), [timeZone]);
    const defaultStartDate = useMemo(() => defaultEndDate.subtract(30, 'day'), [defaultEndDate]);
    
    const [dateHistory, setDateHistory] = useState(defaultStartDate);
    const [dateHistoryEnd, setDateHistoryEnd] = useState(defaultEndDate);
    
    const [insights, setInsights] = useState([]);
    const [mostRecentInsights, setMostRecentInsights] = useState({});
    const [loading, setLoading] = useState(false);
    const [pageChanging, setPageChanging] = useState(false); // For pagination
    const [filterChanging, setFilterChanging] = useState(false); // For filter changes
    const [initialLoad, setInitialLoad] = useState(true); // Track initial load state
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalPages: 0,
        totalCount: 0,
    });
    const resetDateRange = () => {
        // Set back to the default dates
        setDateHistory(defaultStartDate);
        setDateHistoryEnd(defaultEndDate);
        
        // Reset to page 1 and fetch insights
        setPagination({
            ...pagination,
            page: 1,
        });
        
        // Use setTimeout to ensure state updates before fetching
        setTimeout(() => {
            setFilterChanging(true);
            fetchData(1, defaultStartDate, defaultEndDate);
        }, 0);
        
        // No longer in initial load state
        setInitialLoad(false);
    };
    
    // Extracted fetch logic to be reused
    const fetchData = async (page, startDate, endDate) => {
        try {
            // Construct the endpoint with query parameters directly in the URL
            let endpoint =
                `/client/ai-insight-jobs/account/${accountId}/paginated?` +
                `page=${page}&` +
                `pageSize=${pagination.pageSize}&` +
                `startDate=${startDate.format('YYYY-MM-DD')}&` +
                `endDate=${endDate.format('YYYY-MM-DD')}&` +
                `sortBy=createdAt&` +
                `sortOrder=desc&` +
                `isSchedule=true`;
            // Add templateId filter if available
            if (templateId) {
                endpoint += `&templateId=${templateId}`;
            }
            
            const response = await callTokenApi(endpoint, 'GET');

            if (!response) {
                toast.error('Failed to load insights');
                return;
            }

            const responseData = response.data;
            
            if (response.data.code === 200) {
                setInsights(responseData.data);
                setPagination({
                    ...pagination,
                    page: responseData.pagination.page,
                    totalPages: responseData.pagination.totalPages,
                    totalCount: responseData.pagination.totalCount,
                });
                
                // No longer initial load after first successful fetch
                setInitialLoad(false);
            } else {
                toast.error('Failed to load insights');
            }
        } catch (err) {
            toast.error('An error occurred while fetching insights. Please try again.');
            setInitialLoad(false); // Also handle error case
        } finally {
            // Reset all loading states
            setLoading(false);
            setPageChanging(false);
            setFilterChanging(false);
        }
    };
    
    const fetchDataRecentInsight = async () => {
        try {
            let endpoint = `/client/ai-insight-jobs/account/${accountId}/most-recent-insight?`;
            if (templateId) {
                endpoint += `&templateId=${templateId}`;
            }

            const response = await callTokenApi(endpoint, 'GET');

            if (response?.data?.code === 200) {
                setMostRecentInsights(response.data.data);
            } else {
                toast.error('Failed to load recent insights');
            }
        } catch {
            toast.error('An error occurred while fetching recent insights. Please try again.');
        }
    };

    // Common function to handle UI state for data loading
    const prepareForDataLoading = (isFilter = false, isPage = false) => {
        if (isFilter) {
            setFilterChanging(true);
        } else if (isPage) {
            setPageChanging(true);
        } else {
            setLoading(true);
        }
    };

    const fetchInsights = async (page = 1, isPageChange = false, isFilterChange = false) => {
        prepareForDataLoading(isFilterChange, isPageChange);
        fetchData(page, dateHistory, dateHistoryEnd);
        fetchDataRecentInsight();
    };

    const handlePageChange = (page) => {
        setPagination({
            ...pagination,
            page,
        });
        fetchInsights(page, true, false); // true for page change, false for filter change
    };

    const handleFilterChange = () => {
        setPagination({
            ...pagination,
            page: 1,
        });
        fetchInsights(1, false, true); // false for page change, true for filter change
        setInitialLoad(false); // User has explicitly filtered, so no longer in initial state
    };

    // Rerun insight directly with old prompt
    const handleRerunInsight = async (insight) => {
        if (!insight) return;

        const insightTemplateId = insight.templateId || templateId;
        if (!insightTemplateId) {
            toast.error('Template ID not found for this insight');
            return;
        }

        const response = await callTokenApi(
            API_AI_INSIGHTS_JOB_RERUN.replace(':jobId', insight.id),
            'POST',
            { accountId }
        );

        if (response?.data?.code === 200) {
            toast.success('Insight re-run started successfully');
            fetchInsights(pagination.page, false, false);
        } else {
            toast.error(response?.data?.message || 'Failed to re-run insight');
        }
    };

    useEffect(() => {
        fetchInsights();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId]);

    // Listen for pusher event to update totalChargedCredits when job completes
    useEffect(() => {
        const handleJobStatusUpdate = async (data) => {
            if (!data?.jobId || data.status !== 'done') {
                return;
            }

            // Find the insight to update (either the job itself or its parent)
            const insightToUpdate = insights.find(
                (insight) => insight.id === data.jobId || insight.id === data.parentJobId
            );

            if (insightToUpdate) {
                // Fetch updated totalChargedCredits for this specific insight
                try {
                    const response = await callTokenApi(
                        `/client/ai-insight-jobs/${insightToUpdate.id}/reruns`,
                        'GET'
                    );
                    if (response?.data?.code === 200) {
                        const { totalChargedCredits } = response.data;
                        // Update only the specific insight's totalChargedCredits
                        setInsights((prevInsights) =>
                            prevInsights.map((insight) =>
                                insight.id === insightToUpdate.id
                                    ? { ...insight, totalChargedCredits }
                                    : insight
                            )
                        );
                    }
                } catch {
                    // Silent fail - don't disrupt user experience
                }
            }
        };

        subscribe('ai-insight-job-status-update', handleJobStatusUpdate);

        return () => {
            unsubscribe('ai-insight-job-status-update', handleJobStatusUpdate);
        };
    }, [insights]);

    // Determine if any loading state is active
    const isAnyLoading = loading || pageChanging || filterChanging;
    const enableShowCost = localStorage.getItem('show-llm-costs') || window.location.search.includes('show-llm-costs=1');

    return (
        <div className='rule-detail account-connected-detail review-analyse'>
            <div className='cvr-create-new'>
                <h1 className='mb-3'>Historical Insights</h1>
                <p>
                    Review the past analyses completed by the AI Insights Template <strong>{templateName || 'All Templates'}</strong>
                </p>
                
                <div className='d-flex align-items-center gap-2 mb-4'>
                    <span>Filter to a specific date range</span>

                    <CFormGroup className='mb-0'>
                        <CInputGroup>
                            <CLabel className='historical-sync-icon' htmlFor='dateRangeFilter'>
                                <i className='far fa-calendar-alt'></i>
                            </CLabel>

                            <CustomDatePicker
                                today={defaultEndDate}
                                isForm={true}
                                setDateHistory={setDateHistory}
                                setDateHistoryEnd={setDateHistoryEnd}
                                startDate={dateHistory}
                                endDate={dateHistoryEnd}
                                disabled={isAnyLoading}
                            />
                        </CInputGroup>
                    </CFormGroup>

                    <CButton type='button' color='primary' onClick={handleFilterChange} disabled={isAnyLoading}>
                        Apply
                    </CButton>

                    <CButton
                        type='button'
                        color='link'
                        className='text-dark text-underline text-capitalize'
                        onClick={resetDateRange}
                        disabled={isAnyLoading}
                    >
                        Clear Filter
                    </CButton>

                    {enableShowCost && (
                        <div className='tooltip-wrapper'>
                            <HoverInfoIconTooltip className='icon-primary'>
                                <p>Average for the most recent 5 insights</p>
                                <p>Average Token In: {Number(mostRecentInsights.avgInputTokens).toLocaleString('en-US')}</p>
                                <p>Average Tokens Out: {Number(mostRecentInsights.avgOutputTokens).toLocaleString('en-US')}</p>
                                <p>Average Total Tokens: {(Number(mostRecentInsights.avgTotalTokens)).toLocaleString('en-US')}</p>
                                <p>Average Cost Per Analysis: ${Number(mostRecentInsights.avgCostPerAnalysis).toLocaleString('en-US')}</p>
                            </HoverInfoIconTooltip>
                        </div>
                    )}
                </div>

                {/* Content area with loading in the center */}
                <div className='position-relative mt-4'>
                    {/* Loading overlay in the center */}
                    {isAnyLoading && (
                        <div className='position-absolute w-100 h-100 d-flex justify-content-center align-items-center' 
                            style={{
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                zIndex: 10,
                            }}
                        >
                            <CSpinner color='primary' />
                        </div>
                    )}
                    
                    {/* No data message - only show after initial load completed and when user has filtered */}
                    {insights.length === 0 && !initialLoad && (
                        <CAlert color='info'>No insights found for the selected date range. Try adjusting your filters or running new analyses.</CAlert>
                    )}

                    {/* Insight items */}
                    <div className='insights-list'>
                        {insights.map((insight) => (
                            <div key={insight.id} className='accordion list-custom-reports'>
                                <div className='accordion-item' style={{ border: 0 }}>
                                    <div className='list-rules'>
                                        <ViewReviewAnalyse
                                            key={insight.id}
                                            index={insight.id}
                                            insight={insight}
                                            models={models}
                                            onRerun={handleRerunInsight}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pagination - outside of the loading container */}
                {pagination.totalPages > 1 && (
                    <div className='d-flex flex-column justify-content-center align-items-center mt-4'>
                        <CPagination
                            activePage={pagination.page}
                            pages={pagination.totalPages}
                            onActivePageChange={handlePageChange}
                            align='center'
                            disabled={isAnyLoading}
                            className={'ai-insights-pagination'}
                        />
                        <div className='d-flex align-items-center mt-2'>
                            Showing {(pagination.page - 1) * pagination.pageSize + 1} -{' '}
                            {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} insights
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default ReviewAnalyse;
